import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

// Bilingual, click-through Day 1 orientation deck — meant to be projected on a
// screen for students. Always shows English + Japanese together; no language
// toggle. Kept as static in-page data since this deck is fixed presentation
// content, not user data.

const SCHEDULE = [
  { time: '9:00 - 9:30', en: 'Cleaning', ja: '清掃', icon: 'bi-brush' },
  { time: '9:30 - 10:00', en: 'Food Preparation', ja: '朝食準備', icon: 'bi-egg-fried' },
  { time: '10:00 - 10:30', en: 'Warm Up', ja: 'ウォームアップ', icon: 'bi-lightning-charge-fill' },
  { time: '10:30 - 12:00', en: 'Core Session 1', ja: 'コアセッション 1', icon: 'bi-mortarboard-fill' },
  { time: '12:00 - 13:30', en: 'Lunch', ja: '昼食', icon: 'bi-cup-hot-fill' },
  { time: '13:30 - 13:45', en: 'Brushing Teeth', ja: '歯みがき', icon: 'bi-droplet-fill' },
  { time: '13:45 - 14:00', en: 'Cleaning', ja: '清掃', icon: 'bi-brush' },
  { time: '14:00 - 16:00', en: 'Core Session 2', ja: 'コアセッション 2', icon: 'bi-mortarboard-fill' },
  { time: '16:00 - 17:30', en: 'Board Games', ja: 'ボードゲーム', icon: 'bi-dice-5-fill' },
];

const PROGRAMS = [
  { icon: '💡', en: 'Kids Entrepreneurship', ja: 'キッズ・アントレプレナーシップ' },
  { icon: '🤖', en: 'AI Literacy', ja: 'AIリテラシー' },
  { icon: '🎤', en: 'Public Presentation', ja: 'パブリックプレゼンテーション' },
  { icon: '🎮', en: 'E-Sports', ja: 'eスポーツ' },
  { icon: '🔢', en: 'Vedic Mathematics', ja: 'ヴェーダ数学' },
  { icon: '🦾', en: 'Robotics', ja: 'ロボティクス' },
];

const RULES = [
  { icon: 'bi-heart-fill', en: 'Be kind and respectful', ja: '優しく、思いやりを持とう' },
  { icon: 'bi-ear-fill', en: 'Listen and follow instructions', ja: 'お話をよく聞こう' },
  { icon: 'bi-stars', en: "Try your best — mistakes are OK!", ja: '失敗を恐れず、頑張ろう！' },
  { icon: 'bi-people-fill', en: 'Work together as a team', ja: 'チームで協力しよう' },
  { icon: 'bi-bag-check-fill', en: 'Keep your things tidy', ja: '自分の持ち物を整理整頓しよう' },
  { icon: 'bi-emoji-laughing-fill', en: 'Have fun!', ja: '楽しもう！' },
];

const MILESTONES = [
  { pct: '25%', icon: 'bi-star-fill', color: '#FFD166' },
  { pct: '50%', icon: 'bi-stars', color: '#06D6A0' },
  { pct: '75%', icon: 'bi-award-fill', color: '#118AB2' },
  { pct: '100%', icon: 'bi-trophy-fill', color: '#FF6B35' },
];

function Bilingual({ en, ja, size = '2.2rem', jaSize, weight = 900, color = 'white', center = true }) {
  return (
    <div style={{ textAlign: center ? 'center' : 'left' }}>
      <div style={{ fontSize: size, fontWeight: weight, color, lineHeight: 1.25 }}>{en}</div>
      <div style={{ fontSize: jaSize || `calc(${size} * 0.72)`, fontWeight: weight, color, opacity: 0.9, lineHeight: 1.3, marginTop: '0.3em' }}>{ja}</div>
    </div>
  );
}

function SlideShell({ background, children }) {
  return (
    <div style={{
      minHeight: '100vh', width: '100%',
      background,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '3rem 2rem', boxSizing: 'border-box', position: 'relative', overflow: 'hidden',
    }}>
      {children}
    </div>
  );
}

const SLIDES = [
  // 1 — Welcome
  {
    render: () => (
      <SlideShell background="linear-gradient(135deg, #FF6B35 0%, #FFD166 50%, #06D6A0 100%)">
        <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>🌺</div>
        <Bilingual
          en="Welcome to Summer School 2026!"
          ja="サマースクール2026へようこそ！"
          size="clamp(2.2rem, 6vw, 4.2rem)"
        />
        <div style={{ marginTop: '2.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.8rem', background: 'rgba(255,255,255,0.2)', borderRadius: 999, padding: '0.8rem 2rem' }}>
          <i className="bi bi-calendar-event-fill" style={{ fontSize: '1.8rem', color: 'white' }} />
          <span style={{ fontSize: '1.6rem', fontWeight: 800, color: 'white' }}>Day 1 · 1日目</span>
        </div>
      </SlideShell>
    ),
  },
  // 2 — What we're going to do
  {
    render: () => (
      <SlideShell background="linear-gradient(135deg, #073B4C, #118AB2)">
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <i className="bi bi-rocket-takeoff-fill" style={{ fontSize: '3rem', color: '#FFD166' }} />
          <Bilingual en="What We're Going to Do" ja="今週やること" size="clamp(1.8rem, 4.5vw, 3rem)" />
        </div>
        <div className="row g-3" style={{ maxWidth: 1100, width: '100%' }}>
          {PROGRAMS.map((p, i) => (
            <div key={i} className="col-6 col-md-4">
              <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 20, padding: '1.3rem 1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '2.6rem', marginBottom: '0.5rem' }}>{p.icon}</div>
                <div style={{ fontSize: 'clamp(1rem, 1.8vw, 1.3rem)', fontWeight: 900, color: 'white' }}>{p.en}</div>
                <div style={{ fontSize: 'clamp(0.85rem, 1.4vw, 1.05rem)', fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>{p.ja}</div>
              </div>
            </div>
          ))}
        </div>
      </SlideShell>
    ),
  },
  // 3 — Daily Schedule
  {
    render: () => (
      <SlideShell background="linear-gradient(180deg, #FFF3E4, #FFE0C2)">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <i className="bi bi-calendar3" style={{ fontSize: '3rem', color: 'var(--orange)' }} />
          <Bilingual en="Daily Schedule" ja="1日のスケジュール" size="clamp(1.8rem, 4.5vw, 3rem)" color="var(--navy)" />
        </div>
        <div style={{ width: '100%', maxWidth: 900 }}>
          {SCHEDULE.map((s, i) => (
            <div key={i} className="d-flex align-items-center gap-3" style={{
              background: i % 2 === 0 ? 'white' : 'rgba(255,255,255,0.6)',
              borderRadius: 14, padding: '0.6rem 1.2rem', marginBottom: '0.4rem',
            }}>
              <div style={{
                width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, var(--orange), var(--coral))',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.15rem',
              }}>
                <i className={`bi ${s.icon}`} />
              </div>
              <div style={{ fontWeight: 900, color: 'var(--orange)', fontSize: 'clamp(0.9rem, 1.6vw, 1.15rem)', minWidth: 120 }}>{s.time}</div>
              <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 'clamp(1rem, 1.8vw, 1.3rem)' }}>{s.en}</div>
              <div style={{ fontWeight: 700, color: '#64748b', fontSize: 'clamp(0.85rem, 1.6vw, 1.1rem)' }}>{s.ja}</div>
            </div>
          ))}
        </div>
      </SlideShell>
    ),
  },
  // 4 — What we expect from you
  {
    render: () => (
      <SlideShell background="linear-gradient(135deg, #A855F7, #EC4899)">
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <i className="bi bi-hand-thumbs-up-fill" style={{ fontSize: '3rem', color: 'white' }} />
          <Bilingual en="What We Expect From You" ja="みんなに期待すること" size="clamp(1.8rem, 4.5vw, 3rem)" />
        </div>
        <div className="row g-3" style={{ maxWidth: 1100, width: '100%' }}>
          {RULES.map((r, i) => (
            <div key={i} className="col-12 col-md-6">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'rgba(255,255,255,0.15)', borderRadius: 18, padding: '1rem 1.3rem' }}>
                <i className={`bi ${r.icon}`} style={{ fontSize: '2rem', color: '#FFD166', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 'clamp(1rem, 1.8vw, 1.3rem)', fontWeight: 900, color: 'white' }}>{r.en}</div>
                  <div style={{ fontSize: 'clamp(0.85rem, 1.5vw, 1.05rem)', fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>{r.ja}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SlideShell>
    ),
  },
  // 5 — How stamps work
  {
    render: () => (
      <SlideShell background="linear-gradient(135deg, #06D6A0, #118AB2)">
        <div style={{ fontSize: '4.5rem', marginBottom: '1rem' }}>⭐</div>
        <Bilingual en="Earn Stamps Every Session!" ja="毎回のセッションでスタンプをゲット！" size="clamp(1.8rem, 4.5vw, 3rem)" />
        <div style={{ maxWidth: 800, marginTop: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: 'clamp(1.1rem, 2.2vw, 1.6rem)', fontWeight: 700, color: 'white', marginBottom: '0.6rem' }}>
            Every morning &amp; afternoon session, you can earn a stamp. Your stamps add up to your Overall Progress %.
          </div>
          <div style={{ fontSize: 'clamp(1rem, 2vw, 1.4rem)', fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>
            毎回の午前・午後のセッションでスタンプがもらえます。スタンプは「総合進捗」%に加算されます。
          </div>
        </div>
        <div style={{ marginTop: '2.5rem', background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '1.2rem 2rem', textAlign: 'center' }}>
          <div style={{ fontSize: 'clamp(1.3rem, 2.6vw, 2rem)', fontWeight: 900, color: '#FFD166' }}>
            🏆 100% = Certificate! / 100%で修了証！
          </div>
        </div>
      </SlideShell>
    ),
  },
  // 6 — Certificates & Awards
  {
    render: () => (
      <SlideShell background="linear-gradient(135deg, #073B4C, #118AB2)">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <i className="bi bi-gift-fill" style={{ fontSize: '3rem', color: '#FFD166' }} />
          <Bilingual en="Certificates &amp; Awards" ja="修了証とごほうび" size="clamp(1.8rem, 4.5vw, 3rem)" />
        </div>
        <div className="row g-3 justify-content-center mb-4" style={{ maxWidth: 900, width: '100%' }}>
          {MILESTONES.map((m, i) => (
            <div key={i} className="col-6 col-md-3">
              <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 18, padding: '1.2rem 0.5rem', textAlign: 'center' }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%', margin: '0 auto 0.5rem',
                  background: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem', color: 'white',
                }}>
                  <i className={`bi ${m.icon}`} />
                </div>
                <div style={{ fontWeight: 900, fontSize: 'clamp(1.1rem, 2vw, 1.5rem)', color: 'white' }}>{m.pct}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ maxWidth: 800, textAlign: 'center', background: 'white', borderRadius: 20, padding: '1.3rem 2rem' }}>
          <div style={{ fontSize: 'clamp(1rem, 2vw, 1.4rem)', fontWeight: 800, color: 'var(--navy)' }}>
            Finish 100% and choose a gift card — Amazon, Roblox, LINE, and more!
          </div>
          <div style={{ fontSize: 'clamp(0.9rem, 1.8vw, 1.2rem)', fontWeight: 700, color: '#64748b', marginTop: '0.4rem' }}>
            100%達成でギフトカードを選べます — Amazon、Roblox、LINEなど！
          </div>
        </div>
      </SlideShell>
    ),
  },
];

export default function Orientation() {
  const [i, setI] = useState(0);
  const last = SLIDES.length - 1;

  const next = useCallback(() => setI(v => Math.min(v + 1, last)), [last]);
  const prev = useCallback(() => setI(v => Math.max(v - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  return (
    <div style={{ position: 'relative' }}>
      {SLIDES[i].render()}

      {/* Exit link */}
      <Link to="/dashboard" style={{
        position: 'fixed', top: 20, left: 20, zIndex: 10,
        width: 44, height: 44, borderRadius: '50%',
        background: 'rgba(0,0,0,0.35)', color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.3rem', textDecoration: 'none',
      }}>
        <i className="bi bi-x-lg" />
      </Link>

      {/* Slide counter */}
      <div style={{
        position: 'fixed', top: 20, right: 20, zIndex: 10,
        background: 'rgba(0,0,0,0.35)', color: 'white', fontWeight: 800,
        borderRadius: 999, padding: '0.5rem 1.1rem', fontSize: '0.95rem',
      }}>
        {i + 1} / {SLIDES.length}
      </div>

      {/* Prev / Next controls */}
      <button
        onClick={prev}
        disabled={i === 0}
        style={{
          position: 'fixed', left: 20, top: '50%', transform: 'translateY(-50%)', zIndex: 10,
          width: 56, height: 56, borderRadius: '50%', border: 'none',
          background: 'rgba(0,0,0,0.35)', color: 'white', fontSize: '1.5rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1,
        }}
      >
        <i className="bi bi-chevron-left" />
      </button>
      <button
        onClick={next}
        disabled={i === last}
        style={{
          position: 'fixed', right: 20, top: '50%', transform: 'translateY(-50%)', zIndex: 10,
          width: 56, height: 56, borderRadius: '50%', border: 'none',
          background: 'rgba(0,0,0,0.35)', color: 'white', fontSize: '1.5rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: i === last ? 'default' : 'pointer', opacity: i === last ? 0.3 : 1,
        }}
      >
        <i className="bi bi-chevron-right" />
      </button>

      {/* Dots */}
      <div style={{
        position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 10,
        display: 'flex', gap: 8,
      }}>
        {SLIDES.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setI(idx)}
            style={{
              width: idx === i ? 26 : 10, height: 10, borderRadius: 999, border: 'none',
              background: idx === i ? 'white' : 'rgba(255,255,255,0.5)', transition: 'all 0.2s ease', cursor: 'pointer',
            }}
          />
        ))}
      </div>
    </div>
  );
}
